import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminOrder, OrderQueueCounts, OrderSummary, QrScanResponse } from '@hutdrop/shared';
import { api } from './api';

export type OrderQueue = 'new' | 'in_progress' | 'ready' | 'expiring' | 'active' | 'completed' | 'expired' | 'cancelled' | 'all';

// Server-time offset: countdowns use the API's clock, so a wrong PC clock at the counter changes nothing.
let offsetMs = 0;
export function syncServerTime(serverTime: string | undefined) {
  if (serverTime) offsetMs = Date.parse(serverTime) - Date.now();
}
export function useServerNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now() + offsetMs);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() + offsetMs), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function useOrderQueue(queue: OrderQueue, q: string) {
  return useInfiniteQuery({
    queryKey: ['orders', queue, q],
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ queue, limit: '50' });
      if (q) params.set('q', q);
      if (pageParam) params.set('cursor', pageParam);
      const res = await api.request<{ items: OrderSummary[]; nextCursor: string | null; serverTime: string }>(`/v1/admin/orders?${params}`, { auth: true });
      syncServerTime(res.serverTime);
      return res;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // Orders arrive while the counter screen is open.
    refetchInterval: 10_000,
  });
}

export function useOrderCounts(enabled = true) {
  return useQuery({
    queryKey: ['orders', 'counts'],
    enabled,
    queryFn: () => api.request<OrderQueueCounts>('/v1/admin/orders/counts', { auth: true }),
    // Drives the new-order alert, so it keeps checking even when the tab is in the background.
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
  });
}

export function useStaffOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['order', id],
    enabled: !!id,
    queryFn: async () => {
      const order = await api.request<AdminOrder>(`/v1/admin/orders/${id}`, { auth: true });
      syncServerTime(order.serverTime);
      return order;
    },
    refetchInterval: 30_000,
  });
}

export function useOrderActions(id: string) {
  const qc = useQueryClient();
  const run = async (path: string, body: object = {}) => {
    const order = await api.request<AdminOrder>(`/v1/admin/orders/${id}/${path}`, { method: 'POST', auth: true, body });
    syncServerTime(order.serverTime);
    qc.setQueryData(['order', id], order);
    void qc.invalidateQueries({ queryKey: ['orders'] });
    return order;
  };
  return {
    accept: () => run('accept'),
    ready: () => run('ready'),
    complete: () => run('complete', { idCheckAttested: true }),
    cancel: (reason: string) => run('cancel', { reason }),
  };
}

export async function scanCode(token: string) {
  const res = await api.request<QrScanResponse>('/v1/admin/orders/scan', { method: 'POST', auth: true, body: { token } });
  syncServerTime(res.order.serverTime);
  return res;
}
